import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

import withQnAPagination from '@catalogo/service-americanas-product/src/questions-and-answers/hoc/pagination';

import Loading from '@catalogo/ui-americanas-desktop-loading';
import Button from '@catalogo/ui-americanas-desktop-button';

import { MakeQuestion } from './components/make-question';
import { Questions } from './components/questions';
import { SearchField } from './components/search-field';
import { OrderBy } from './components/order-by';

const Wrapper = styled.div`
  padding: 10px;
  background: #fff;
`;

const WrapperHeader = styled.div`
  margin-top: 10px;
`;

const WrapperTitle = styled.div`
  padding-bottom: 18px;
  margin-bottom: 12px;
  border-bottom: 1px solid #eee;
`;

const WrapperFields = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 55px;

  > div {
    flex: 1 1 0;
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;

    &:last-child {
      justify-content: flex-end;
    }
  }
`;

const WrapperLoading = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  min-height: 80px;
`;

const Title = styled.p`
  flex: 4;
  font-size: 16px;
  color: ${({ theme }) => theme.textColor};
  font-weight: ${({ theme }) => theme.fontWeight.title};
`;

const MessageTermFound = styled.p`
  color: #666;
  margin: 5px 0;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 16px;
  width: 100%;
`;

const ButtonUI = styled(Button)`
  border: 2px solid ${({ theme }) => theme.primaryColor};
  margin: 20px auto 0px;
  color: ${({ theme }) => theme.primaryColor};
  font-weight: ${({ theme }) => theme.btnTextWeigth};
  cursor: pointer;

  & :hover {
    color: ${({ theme }) => theme.primaryColor};
  }
`;

const renderFetchMoreButton = props => {
  const { loading, nextOffset, total, onPaginationClick } = props;
  if (loading.all) {
    return null;
  }

  if (loading.button) {
    return (
      <WrapperLoading>
        <Loading position="relative" />
      </WrapperLoading>
    );
  }

  if (nextOffset < total) {
    return (
      <ButtonUI
        width="200px"
        height="45px"
        title="ver mais perguntas"
        borderRadius="8px"
        fontSize="16px"
        onClick={() => onPaginationClick(nextOffset)}
      />
    );
  }

  return null;
};

export const QuestionsAndAnswersList = ({
  questionsAndAnswers,
  fetchMore,
  loading,
  hasCustomerQuestions,
  productId,
  hasOffers,
  brandText,
  onPaginationClick,
}) => {
  const [term, setTerm] = useState('');
  const [messageTermFound, setMessageTermFound] = useState('');
  const [totalQuestions, setTotalQuestion] = useState(null);
  const { questions, meta } = questionsAndAnswers;
  const { total, limit, offset } = meta;
  const nextOffset = offset + limit;

  useEffect(() => {
    if (totalQuestions === null) {
      return setTotalQuestion(total);
    }
  }, [total]);

  /* istanbul ignore next  */
  useEffect(() => {
    if (loading?.all && term) {
      setMessageTermFound('');
    } else if (!loading?.all && term && total) {
      const str = brandText.statusTermFound(total);
      setMessageTermFound(str);
    }
    return () => setMessageTermFound('');
  }, [loading]);

  /* istanbul ignore next */
  const onChangeTerm = useCallback(e => setTerm(e.target.value), []);

  const renderButton = renderFetchMoreButton({ loading, nextOffset, total, onPaginationClick });

  const title = `${totalQuestions} pergunta${totalQuestions > 1 ? 's' : ''} sobre esse produto`;

  return (
    <Wrapper>
      {hasOffers && (
        <MakeQuestion
          title={brandText.title.default}
          hasCustomerQuestions={hasCustomerQuestions}
          productId={productId}
        />
      )}
      <WrapperHeader>
        <WrapperTitle>
          <Title>{title}</Title>
        </WrapperTitle>
        <WrapperFields>
          <div>
            <SearchField fetchMore={fetchMore} onChange={onChangeTerm} term={term} />
            <MessageTermFound>{messageTermFound}</MessageTermFound>
          </div>
          <div>
            <OrderBy fetchMore={fetchMore} offset={offset} limit={limit} total={total} />
          </div>
        </WrapperFields>
      </WrapperHeader>
      <Questions
        brandText={brandText}
        productId={productId}
        questions={questions}
        loading={loading.all}
        totalQuestions={totalQuestions}
        totalTermFound={total}
        term={term}
      />
      {renderButton}
    </Wrapper>
  );
};

export default withQnAPagination(QuestionsAndAnswersList);
